import { log } from '@/log'
import { SessionApiService } from '@/services/api/session'
import { SessionApiErrorResponse } from '@/services/api/session/response'
import {
  DataFulfilledSessionPayload,
  isDataFulfilledSessionPayload,
} from '@/services/session/session-state'
import { SessionStateStore } from '@/services/session/session-state-store'
import { SyncSession } from '@/services/session/sync-session'

const namespace = 'load-session-data'

/**
 * データが充足したセッションの存在を保証する
 * - storeのセッションデータが充足していれば副作用なしで返す
 * - 充足していなければ、セッション存在保証の後にloadDataを実行する
 */
export class LoadSessionData {
  static callWithDataFulfilledSession<T>(
    callback: (payload: DataFulfilledSessionPayload) => Promise<T>
  ): Promise<T> {
    return LoadSessionData.getInstance().load().then(callback)
  }

  static getInstance(): LoadSessionData {
    if (!LoadSessionData.instance) {
      LoadSessionData.instance = new LoadSessionData()
    }

    return LoadSessionData.instance
  }

  private static instance: LoadSessionData

  // 重複実行を制御するためのキャッシュ
  private loadPromise: Promise<DataFulfilledSessionPayload> | null = null

  private constructor(
    private readonly sessionApiService: SessionApiService = new SessionApiService(),
    private readonly sessionStateStore: SessionStateStore = SessionStateStore.getInstance(),
    private readonly syncSession: SyncSession = SyncSession.getInstance()
  ) {}

  load(): Promise<DataFulfilledSessionPayload> {
    if (this.loadPromise) {
      return this.loadPromise
    }

    this.loadPromise = this.innerLoad()
      .then((payload) => {
        log.debug({ service: `${namespace}/load`, payload })

        return payload
      })
      .finally(() => {
        // 次の実行時にはキャッシュを利用しないようにする
        // API側がエラーでセッションデータを破棄した際に再読み込みを行う意図
        // 通常は次回実行時にはstoreから読めるはずなのでパフォーマンス的には懸念はない
        this.loadPromise = null
      })

    return this.loadPromise
  }

  /**
   * 1. すでにstoreのセッションデータが充足していればそれを返す
   * 2. なければAPIを呼び出してセッションデータをロードする
   */
  private async innerLoad(): Promise<DataFulfilledSessionPayload> {
    await this.syncSession.onSynced(() => Promise.resolve())

    const current = this.readFulfilledDataFromStore()
    if (current) {
      return current
    }

    await this.loadData()

    const loaded = this.readFulfilledDataFromStore()
    if (loaded) {
      return loaded
    }

    throw new Error('Session data is not fulfilled after loadData')
  }

  private readFulfilledDataFromStore(): DataFulfilledSessionPayload | null {
    const payload = this.sessionStateStore.readSession()
    if (!payload) {
      return null
    }

    if (!isDataFulfilledSessionPayload(payload)) {
      return null
    }

    return payload
  }

  private async loadData(): Promise<void> {
    const result = await this.sessionApiService.loadData()

    return result.fold(
      (error: SessionApiErrorResponse) => {
        throw new Error(`${error.errorCode}: ${error.message}`)
      },
      () => Promise.resolve()
    )
  }
}
